import { useState } from "react";
import { cn } from "@/lib/utils";
import type { DebugExtractionInfo } from "../types";

// 7-section diagnostic panel from Pages/Upload.razor. Shown only when
// DebugInfo is populated (the AI parser fills it). Defaults to collapsed.
export function DebugPanel({ info }: { info: DebugExtractionInfo }) {
  const [open, setOpen] = useState(false);

  return (
    <section className="border border-rule">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between border-b border-rule bg-ink/[0.02] px-6 py-3"
      >
        <div className="label flex items-center gap-2">
          <span aria-hidden>{open ? "▾" : "▸"}</span>
          Debug Information
        </div>
        <span className="font-mono text-[0.7rem] uppercase tracking-widest text-ink-faint">
          {open ? "Collapse" : "Expand"}
        </span>
      </button>

      {open && (
        <div className="space-y-6 px-6 py-6">
          <div className="font-mono text-[0.78rem] text-ink-muted">
            This debug panel shows the raw extraction data for troubleshooting
            purposes.
          </div>

          <Section
            title="Raw Annotations Extracted"
            caption={`${info.rawAnnotations.length} total annotations found across all pages`}
          >
            <Json data={info.rawAnnotations} />
          </Section>

          <Section
            title="Filtered Annotations (Valid Content Only)"
            caption={`${info.filteredAnnotations.length} annotations after filtering`}
          >
            <Json data={info.filteredAnnotations} />
          </Section>

          <Section
            title="DOB Markup Table Objections"
            caption={`${info.dobMarkupObjections.length} objections from DOB markup tables`}
          >
            <Json data={info.dobMarkupObjections} />
          </Section>

          <Section
            title="Standard Table Objections"
            caption={`${info.standardTableObjections.length} objections from standard tables`}
          >
            <Json data={info.standardTableObjections} />
          </Section>

          <Section
            title="Annotation Block Objections"
            caption={`${info.annotationBlockObjections.length} objections from [ANNOTATIONS] blocks`}
          >
            <Json data={info.annotationBlockObjections} />
          </Section>

          {info.rawTableRegionRows.length > 0 && (
            <Section
              title="Raw Table Region Extraction (Tabula)"
              caption={(() => {
                const maxCols = Math.max(
                  0,
                  ...info.rawTableRegionRows.map((r) => r.cells.length),
                );
                return `${info.rawTableRegionRows.length} rows, ${maxCols} columns extracted from user-selected regions`;
              })()}
            >
              <TabulaTable rows={info.rawTableRegionRows} />
            </Section>
          )}

          <Section
            title="Final Merged Objections (After Deduplication)"
            caption={`${info.finalMergedObjections.length} final objections after merging and deduplication`}
          >
            <Json data={info.finalMergedObjections} />
          </Section>

          {info.processingNotes.length > 0 && (
            <Section title="Processing Notes" caption="">
              <ul className="ml-4 list-disc space-y-1 font-sans text-[0.875rem] text-ink-muted">
                {info.processingNotes.map((n, i) => (
                  <li key={i}>{n}</li>
                ))}
              </ul>
            </Section>
          )}
        </div>
      )}
    </section>
  );
}

function Section({
  title,
  caption,
  children,
}: {
  title: string;
  caption: string;
  children: React.ReactNode;
}) {
  return (
    <details className="border border-rule">
      <summary className="cursor-pointer list-none border-b border-rule bg-paper px-4 py-2">
        <div className="flex items-baseline justify-between gap-4">
          <div className="label">{title}</div>
          {caption && (
            <div className="font-mono text-[0.7rem] tracking-wider text-ink-faint">
              {caption}
            </div>
          )}
        </div>
      </summary>
      <div className="px-4 py-3">{children}</div>
    </details>
  );
}

function Json({ data }: { data: unknown }) {
  return (
    <pre
      className={cn(
        "max-h-[280px] overflow-auto border border-rule bg-paper px-3 py-2",
        "font-mono text-[0.72rem] leading-relaxed text-ink-muted",
      )}
    >
      {JSON.stringify(data, null, 2)}
    </pre>
  );
}

function TabulaTable({
  rows,
}: {
  rows: { pageNumber: number; rowIndex: number; cells: string[] }[];
}) {
  const maxCols = Math.max(0, ...rows.map((r) => r.cells.length));
  return (
    <div className="max-h-[320px] overflow-auto">
      <table className="w-full border border-rule font-mono text-[0.78rem]">
        <thead className="bg-ink/[0.03]">
          <tr>
            <th className="border-b border-r border-rule px-2 py-1 text-left text-ink-muted">Pg</th>
            <th className="border-b border-r border-rule px-2 py-1 text-left text-ink-muted">Row</th>
            {Array.from({ length: maxCols }, (_, i) => (
              <th
                key={i}
                className="border-b border-r border-rule px-2 py-1 text-left text-ink-muted"
              >
                Col {i}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>
              <td className="border-b border-r border-rule px-2 py-1 text-ink-faint">
                {r.pageNumber}
              </td>
              <td className="border-b border-r border-rule px-2 py-1 text-ink-faint">
                {r.rowIndex}
              </td>
              {Array.from({ length: maxCols }, (_, j) => (
                <td key={j} className="border-b border-r border-rule px-2 py-1">
                  {r.cells[j]?.trim() || (
                    <span className="text-ink-faint">—</span>
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
